import type {
  EventTileCoordinates,
  RandomEventEngineState,
  ActiveRandomEvent,
} from "../../models/randomEvent.ts";
import type { RandomEventDefinition } from "./eventDefinitions.ts";

export interface ActivationContext {
  currentTurn: number;
  clanTile: EventTileCoordinates;
  resolveEventTile?: (eventId: string) => EventTileCoordinates | null;
}

export type RandomEventLifecycleNotification =
  | {
      type: "eventStarted";
      eventId: string;
      startedAtTurn: number;
      tile?: EventTileCoordinates;
    }
  | { type: "eventTileEntered"; eventId: string; tile: EventTileCoordinates }
  | { type: "eventFinished"; eventId: string; reason: "expired" };

export interface RandomEventTransitionResult {
  state: RandomEventEngineState;
  notifications: RandomEventLifecycleNotification[];
}

export interface ProcessTurnEndOptions {
  allowNewActivations?: boolean;
}

export function processTurnEnd(
  state: RandomEventEngineState,
  definitions: RandomEventDefinition[],
  context: ActivationContext,
  random: () => number,
  options: ProcessTurnEndOptions = {},
): RandomEventTransitionResult {
  const allowNewActivations = options.allowNewActivations !== false;
  const notifications: RandomEventLifecycleNotification[] = [];

  if (state.activeEvent) {
    const expiry = applyTurnExpiry(state, notifications);
    if (!expiry.expired) {
      return { state: expiry.state, notifications };
    }

    if (!allowNewActivations) {
      return { state: expiry.state, notifications };
    }

    return tryActivateEvent(
      expiry.state,
      definitions,
      context,
      random,
      notifications,
    );
  }

  if (!allowNewActivations) {
    return { state, notifications };
  }

  return tryActivateEvent(state, definitions, context, random, notifications);
}

/** Activate without a probability roll (map timeline `startEvent`). */
export function forceActivateEvent(
  state: RandomEventEngineState,
  definition: RandomEventDefinition,
  context: ActivationContext,
  tile?: EventTileCoordinates,
  random: () => number = () => 0,
): RandomEventTransitionResult {
  if (state.activeEvent) {
    return { state, notifications: [] };
  }

  if (definition.tileBound && !tile) {
    return { state, notifications: [] };
  }

  return activateDefinition(definition, context, tile, random, []);
}

function applyTurnExpiry(
  state: RandomEventEngineState,
  notifications: RandomEventLifecycleNotification[],
): { state: RandomEventEngineState; expired: boolean } {
  const active = state.activeEvent;
  if (!active || active.turnsRemaining === undefined) {
    return { state, expired: false };
  }

  const nextRemaining = active.turnsRemaining - 1;
  if (nextRemaining > 0) {
    return {
      state: {
        activeEvent: { ...active, turnsRemaining: nextRemaining },
      },
      expired: false,
    };
  }

  notifications.push({
    type: "eventFinished",
    eventId: active.id,
    reason: "expired",
  });

  return {
    state: { activeEvent: null },
    expired: true,
  };
}

function tryActivateEvent(
  state: RandomEventEngineState,
  definitions: RandomEventDefinition[],
  context: ActivationContext,
  random: () => number,
  notifications: RandomEventLifecycleNotification[],
): RandomEventTransitionResult {
  for (const definition of definitions) {
    if (random() >= definition.probability) {
      continue;
    }

    let tile: EventTileCoordinates | undefined;
    if (definition.tileBound) {
      const resolved = context.resolveEventTile?.(definition.id) ?? null;
      if (!resolved) {
        continue;
      }
      tile = resolved;
    }

    return activateDefinition(
      definition,
      context,
      tile,
      random,
      notifications,
    );
  }

  return { state, notifications };
}

function activateDefinition(
  definition: RandomEventDefinition,
  context: ActivationContext,
  tile: EventTileCoordinates | undefined,
  random: () => number,
  notifications: RandomEventLifecycleNotification[],
): RandomEventTransitionResult {
  const turnsRemaining = resolveExpiresAfterTurns(definition, random);

  const activeEvent: ActiveRandomEvent = {
    id: definition.id,
    startedAtTurn: context.currentTurn,
    tile,
    turnsRemaining,
    tileEnteredEmitted: false,
  };

  notifications.push({
    type: "eventStarted",
    eventId: definition.id,
    startedAtTurn: context.currentTurn,
    tile,
  });

  return {
    state: { activeEvent },
    notifications,
  };
}

function resolveExpiresAfterTurns(
  definition: RandomEventDefinition,
  random: () => number,
): number | undefined {
  if (definition.expiresAfterTurnsRange) {
    const { min, max } = definition.expiresAfterTurnsRange;
    return rollInclusiveInt(min, max, random);
  }

  return definition.expiresAfterTurns;
}

function rollInclusiveInt(
  min: number,
  max: number,
  random: () => number,
): number {
  const span = max - min + 1;
  const roll = random();
  const index = roll >= 1 ? span - 1 : Math.floor(roll * span);
  return min + index;
}
